/**
 * Faune.
 *
 * Aucun animal n'est hostile au joueur. Ce qui les distingue n'est pas leur
 * vitesse mais leur FACON DE VIVRE, decrite espece par espece dans
 * `shared/data/animaux.ts` (`PROFILS`) et appliquee ici:
 *
 * - le cerf, le renne, la chevre, le loup et le chameau vivent en TROUPEAU: ils
 *   se rejoignent, gardent leurs distances, broutent ensemble et fuient ensemble
 *   (l'alarme se propage de voisin en voisin);
 * - le herisson et la tortue se mettent en BOULE plutot que de detaler;
 * - le sanglier GARDE sa place: il fait face au lieu de reculer, sans menace;
 * - personne ne chasse personne: les animaux JOUENT. Un animal curieux va a la
 *   rencontre du premier venu, de son espece ou non, le rejoint, lui tourne
 *   autour sans jamais le rattraper, l'invite a en faire autant, puis repart.
 *   Deux lapins se poursuivent en rond, un cerf baisse la tete vers un ecureuil,
 *   deux dauphins font des tonneaux. Aucun animal ne meurt ni ne souffre;
 * - l'aigle PLANE en grands cercles tres haut;
 * - le hibou et la luciole sont NOCTURNES: ils dorment le jour;
 * - le dauphin, le poisson et le phoque NAGENT et ne sortent pas de l'eau; le
 *   canard flotte a la surface;
 * - la mouette, l'oiseau, le papillon et le hibou se POSENT pour picorer.
 *
 * `majAnimaux` mute le tableau sur place et n'alloue rien dans la boucle chaude
 * (que des nombres), parce qu'elle tourne 20 fois par seconde sur trois cents
 * individus, aussi bien dans le navigateur que sur le serveur.
 *
 * APPRIVOISER, JAMAIS CAPTURER. On ne chasse pas et on n'attrape personne: un
 * animal devient FAMILIER parce qu'on l'a nourri plusieurs fois, a des moments
 * differents (`nourrir`, `REPAS_APPRIVOISEMENT`). Un familier ne fuit plus, il
 * suit celui qui l'a nourri, et c'est ainsi — sur ses pattes — qu'il rejoint
 * l'enclos ou la maison ou il va vivre. Un animal qui a un FOYER (`ancre`) sort
 * de la vie sauvage: il deambule chez lui, vient accueillir celui qui rentre et
 * dort dans son coin (`majFoyer`). Les regles d'admission, elles, sont dans
 * shared/sim/zoo.ts: ce fichier-ci ne connait pas les batiments.
 */
import { CARTE_X, CARTE_Z, ILE2_COTE, ILE2_X0, ILE2_Z0, SEA_LEVEL, WORLD_SIZE } from '../constants.ts'
import type { ItemId } from '../types/items.ts'
import type { Animal, AnimalDef, AnimalKind, EtatAnimal } from '../types/monde.ts'
import type { ITerrain, Vec2 } from '../types/world.ts'
import type { ProfilAnimal } from '../data/animaux.ts'
import {
  ALTITUDE_REPOS,
  ALTITUDE_VOL,
  ANIMAUX,
  ESPECES_VOLANTES,
  IMMERSION,
  POPULATION,
  POPULATION_ILE2,
  PROFILS,
  PROFONDEUR_MIN,
} from '../data/animaux.ts'
import { Alea, dist, dist2 } from './alea.ts'
import type { Horloge } from './temps.ts'

/** Marge sans animal au bord du monde, en metres. */
const MARGE_MONDE = 3

/** Pente maximale franchissable par un animal terrestre. */
const PENTE_ANIMAL = 0.9

/**
 * Distance a laquelle on considere la cible atteinte.
 *
 * C'est un PLANCHER: le rayon reel tient compte du pas du tick (voir
 * `rayonArrivee`). Un animal loin du joueur n'est mis a jour qu'un tick sur
 * huit, et sa foulee depasse alors ce demi-metre: il sautait par-dessus sa
 * cible, revenait, resautait, et tournoyait autour d'un point sans jamais
 * l'atteindre.
 */
const ARRIVE = 0.6

/** Distance a laquelle un animal apprivoise se tient du joueur. */
const DISTANCE_SUIVI = 1.5

/** Portee de l'appat. */
const PORTEE_APPAT = 6

/** Portee au-dela de laquelle un animal qui suit abandonne. */
const PORTEE_ABANDON = 12

/** Duree du suivi apres un repas, en secondes. */
export const DUREE_SUIVI = 25

// ------------------------------------------------------------ apprivoisement

/**
 * Repas necessaires pour qu'un animal devienne FAMILIER.
 *
 * Trois: assez pour que l'animal se merite, assez peu pour qu'un enfant y
 * arrive dans la meme partie. On ne capture rien dans ce jeu — le proprietaire
 * l'a tranche — donc c'est la seule facon d'avoir un animal a soi.
 */
export const REPAS_APPRIVOISEMENT = 3

/**
 * Delai minimal entre deux repas COMPTES, en millisecondes de temps de jeu.
 *
 * Sans lui, trois taps dans la meme seconde apprivoisaient une bete: la
 * mecanique serait devenue un bouton, pas une relation. Quinze secondes de jeu,
 * c'est le temps d'aller cueillir autre chose et de revenir.
 */
export const DELAI_REPAS = 15_000

/** Portee a laquelle un animal familier vient d'office au joueur. */
const PORTEE_FAMILIER = 10

/** Portee au-dela de laquelle un familier renonce a suivre. */
const PORTEE_ABANDON_FAMILIER = 26

/** Distance a laquelle un animal de compagnie vient accueillir le joueur. */
const PORTEE_ACCUEIL = 6

/** Vrai si l'animal a ete apprivoise: il ne fuit plus, il suit. */
export function estFamilier(a: Animal): boolean {
  return a.repas >= REPAS_APPRIVOISEMENT
}

/** Repas restants avant l'apprivoisement. */
export function repasRestants(a: Animal): number {
  const reste = REPAS_APPRIVOISEMENT - a.repas
  return reste > 0 ? reste : 0
}

/**
 * Ecarts essayes pour longer un obstacle, en radians, du plus doux au plus
 * franc. Ils s'ajoutent a l'esquive DEJA tenue par l'animal (`Animal.esquive`)
 * et du meme cote qu'elle: on rouvre le contournement en cours au lieu de
 * rejouer la liste depuis zero, ce qui faisait osciller l'animal d'un tick a
 * l'autre entre « tout droit » et « a gauche ».
 */
const ECARTS = [0, 0.35, 0.7, 1.2, 1.8]

/**
 * Vitesse a laquelle une esquive se relache, en radians par seconde.
 *
 * Trop vite, l'animal revient se cogner au tick suivant et vibre; trop
 * lentement, il continue de longer une cote qu'il a depuis longtemps quittee.
 */
const RELACHE_ESQUIVE = 0.9

/**
 * Demi-ouverture du cone dans lequel un animal tire sa prochaine etape, en
 * radians, autour de son cap actuel. Elle s'ouvre a chaque essai infructueux.
 */
const CONE_ERRANCE = 0.75

/**
 * Ecart de contournement au-dela duquel la cible est declaree hors d'atteinte,
 * en radians. Un animal qui avance a soixante degres de la ou il veut aller ne
 * s'y rend pas: il rabote une cote. Mieux vaut renoncer et se choisir un but
 * dans la direction ou il va deja.
 */
const ESQUIVE_ABANDON = 1

/** Rayon du cercle que decrit un planeur, en metres. */
const RAYON_PLANE = 26

/** Duree d'une partie de jeu entre deux animaux, en secondes. */
const JEU_MIN = 4
const JEU_MAX = 9

/** Chance qu'un animal curieux aille voir un compagnon en fin de deplacement. */
const CHANCE_JEU = 0.12

/** Chance qu'un troupeau rassemble parte en partie de jeu plutot que brouter. */
const CHANCE_JEU_TROUPEAU = 0.09

/**
 * Ecart minimal, en metres, en deca duquel on ne corrige plus sa place dans le
 * groupe. Sans cette zone morte, une bete deja bien placee corrigeait de deux
 * centimetres a chaque tick, chaque fois dans une autre direction, et le
 * troupeau entier fremissait sur place.
 */
const BESOIN_MIN = 0.4

/**
 * Zone morte reelle: jamais plus petite que la foulee du tick. Une bete rapide
 * mise a jour un tick sur huit avance d'un metre a chaque reveil; lui demander
 * de corriger vingt centimetres, c'est la faire osciller de part et d'autre.
 */
function besoinMin(vitesse: number, dt: number): number {
  const pas = vitesse * dt * 0.8
  return pas > BESOIN_MIN ? pas : BESOIN_MIN
}

/**
 * Vitesse angulaire visee quand deux animaux se tournent autour, en radians par
 * seconde: un tour en six secondes environ. C'est le rayon du cercle qui
 * s'adapte, pas la vitesse de l'animal.
 */
const OMEGA_JEU = 0.5

/**
 * Cadence de l'IA selon la distance au joueur.
 *
 * Pres du joueur (moins de PORTEE_FINE metres, soit bien plus que ce que la
 * camera montre) chaque animal est mis a jour a chaque tick. Plus loin, il n'est
 * mis a jour qu'un tick sur quatre, puis un tick sur huit, avec un `dt`
 * multiplie d'autant: la vitesse moyenne et les durees d'etat sont identiques,
 * seule la finesse du trajet baisse — et a cette distance un animal fait
 * quelques pixels. Le rang dans le sous-cycle vient de la phase, propre a chaque
 * individu: les mises a jour se repartissent d'elles-memes, sans a-coup collectif.
 */
const PORTEE_FINE = 45
const PORTEE_MOYENNE = 130

// ------------------------------------------------------------- grille spatiale

/**
 * Grille uniforme des animaux, reconstruite au debut de chaque tick.
 *
 * Sans elle, la vie sociale (troupeau, banc, jeu) comparait chaque animal a
 * tous les autres: 347 x 347 paires par tick, soit le poste le plus cher de
 * toute la simulation. La grille ramene chaque recherche a une poignee de
 * cases. Les tampons sont des `Int32Array` reutilises: la boucle chaude
 * n'alloue rien.
 */
const CELLULE = 25
/*
 * LA GRILLE COUVRE LA CARTE, ET ELLE EST RECTANGULAIRE.
 *
 * Elle etait carree et taillee sur l'ile 1 (`ceil(WORLD_SIZE/25)+1 = 21`), et
 * surtout `cellule` CLAMPE les coordonnees hors bornes: chaque animal de l'ile
 * 2 serait tombe dans la case (20,20), tous ensemble, et s'y serait groupe avec
 * les betes de l'ile 1 a un kilometre de la. Ce n'est pas une perte de vitesse,
 * c'est un troupeau qui se forme entre deux iles.
 *
 * POURQUOI L'ILE 1 N'EN BOUGE PAS D'UN BIT, et cela vaut d'etre ecrit: ce dont
 * la physique depend est l'ORDRE de parcours des voisins — les sommes
 * flottantes de `scannerGroupe` ne sont pas associatives — et cet ordre vient
 * des boucles `for cz / for cx` et de la STABILITE du tri par comptage, dont
 * aucun ne depend du nombre de cases. Les animaux de l'ile 1 vivent dans
 * [3, 497]², donc dans les cases 0..19 des deux grilles, et le clamp ne se
 * declenchait deja jamais pour eux.
 */
const COTE_X = Math.ceil(CARTE_X / CELLULE) + 1
const COTE_Z = Math.ceil(CARTE_Z / CELLULE) + 1
const NB_CELLULES = COTE_X * COTE_Z
let grDebut = new Int32Array(NB_CELLULES + 1)
let grCurseur = new Int32Array(NB_CELLULES + 1)
let grOrdre = new Int32Array(0)

function cellule(x: number, z: number): number {
  let ix = Math.floor(x / CELLULE)
  let iz = Math.floor(z / CELLULE)
  if (ix < 0) ix = 0
  else if (ix >= COTE_X) ix = COTE_X - 1
  if (iz < 0) iz = 0
  else if (iz >= COTE_Z) iz = COTE_Z - 1
  return iz * COTE_X + ix
}

function construireGrille(animaux: Animal[]): void {
  const n = animaux.length
  if (grOrdre.length < n) grOrdre = new Int32Array(n + 64)
  grDebut.fill(0)
  for (let i = 0; i < n; i++) {
    const a = animaux[i] as Animal
    const c = cellule(a.pos.x, a.pos.z) + 1
    grDebut[c] = (grDebut[c] as number) + 1
  }
  for (let c = 0; c < NB_CELLULES; c++) {
    grDebut[c + 1] = (grDebut[c + 1] as number) + (grDebut[c] as number)
  }
  grCurseur.set(grDebut)
  for (let i = 0; i < n; i++) {
    const a = animaux[i] as Animal
    const c = cellule(a.pos.x, a.pos.z)
    grOrdre[grCurseur[c] as number] = i
    grCurseur[c] = (grCurseur[c] as number) + 1
  }
}

/** Bornes de cases a balayer autour de (x, z) pour un rayon donne. */
let bx0 = 0
let bx1 = 0
let bz0 = 0
let bz1 = 0

function bornes(x: number, z: number, rayon: number): void {
  bx0 = Math.floor((x - rayon) / CELLULE)
  bx1 = Math.floor((x + rayon) / CELLULE)
  bz0 = Math.floor((z - rayon) / CELLULE)
  bz1 = Math.floor((z + rayon) / CELLULE)
  if (bx0 < 0) bx0 = 0
  if (bz0 < 0) bz0 = 0
  if (bx1 >= COTE_X) bx1 = COTE_X - 1
  if (bz1 >= COTE_Z) bz1 = COTE_Z - 1
}

// --------------------------------------------------------------- praticabilite

/**
 * Vrai si (x, z) est sur UNE des deux iles, marges comprises.
 *
 * C'EST L'UNION DES DEUX BOITES, SURTOUT PAS LA CARTE ENTIERE, et c'est la
 * decision qui protege l'ile 1. En ouvrant la carte, les sept cents metres de
 * haute mer deviendraient un sol legal pour `placeValide`: `Carte.hauteur` y
 * rend une rampe douce jusqu'au fond du large, donc la profondeur minimale des
 * betes aquatiques y est satisfaite partout. Les dauphins, les phoques et les
 * mouettes de l'ile 1 — aujourd'hui retenus au bord par le rejet de `avancer` —
 * partiraient vers le large. Ce serait une divergence de COMPORTEMENT sur
 * l'ile 1, sans le moindre changement de graine, et aucun test de semis ne la
 * verrait.
 *
 * Les deux boites sont disjointes en x (500 contre 1200), donc c'est deux
 * comparaisons et un court-circuit: cette fonction est appelee plusieurs fois
 * par animal et par tick.
 */
function dansLeMonde(x: number, z: number): boolean {
  if (x >= MARGE_MONDE && x <= WORLD_SIZE - MARGE_MONDE) {
    return z >= MARGE_MONDE && z <= WORLD_SIZE - MARGE_MONDE
  }
  if (x >= ILE2_X0 + MARGE_MONDE && x <= ILE2_X0 + ILE2_COTE - MARGE_MONDE) {
    return z >= ILE2_Z0 + MARGE_MONDE && z <= ILE2_Z0 + ILE2_COTE - MARGE_MONDE
  }
  return false
}

/** Les bornes de l'ile ou se trouve ce point: [min, max] sur x et sur z. */
function boiteDeLIle(x: number): { x0: number; x1: number; z0: number; z1: number } {
  if (x >= ILE2_X0) {
    return { x0: ILE2_X0, x1: ILE2_X0 + ILE2_COTE, z0: ILE2_Z0, z1: ILE2_Z0 + ILE2_COTE }
  }
  return { x0: 0, x1: WORLD_SIZE, z0: 0, z1: WORLD_SIZE }
}

/** Vrai si un animal terrestre peut se tenir la: hors de l'eau, pas une falaise. */
export function solAnimal(terrain: ITerrain, x: number, z: number): boolean {
  if (!dansLeMonde(x, z)) return false
  if (terrain.estEau(x, z)) return false
  return terrain.pente(x, z) <= PENTE_ANIMAL
}

/**
 * Vrai si une espece donnee peut se tenir en (x, z). C'est le test a utiliser
 * partout: un dauphin doit etre dans l'eau, un lapin hors de l'eau, une mouette
 * accepte de survoler la mer et un oiseau non.
 */
export function placeValide(terrain: ITerrain, kind: AnimalKind, x: number, z: number): boolean {
  const p = PROFILS[kind]
  switch (p.milieu) {
    case 'terre':
      return solAnimal(terrain, x, z)
    case 'air':
      if (!dansLeMonde(x, z)) return false
      return p.survoleEau || !terrain.estEau(x, z)
    default: {
      // eau et surface: il faut assez de fond.
      if (!dansLeMonde(x, z)) return false
      return SEA_LEVEL - terrain.hauteur(x, z) >= PROFONDEUR_MIN[kind]
    }
  }
}

/** Vrai si l'etat pose l'animal au sol (ou au repos sur son perchoir). */
function auRepos(etat: EtatAnimal): boolean {
  return etat === 'dort' || etat === 'broute' || etat === 'boule' || etat === 'garde'
}

/**
 * Hauteur ou poser un animal, en tenant compte de son etat: une mouette en vol
 * est a six metres, la meme mouette posee sur le sable est au sol, un poisson
 * est sous la surface de la mer.
 */
export function yAnimal(terrain: ITerrain, kind: AnimalKind, etat: EtatAnimal, x: number, z: number): number {
  const p = PROFILS[kind]
  if (p.milieu === 'eau' || p.milieu === 'surface') return SEA_LEVEL - IMMERSION[kind]
  const sol = terrain.hauteur(x, z)
  if (p.milieu !== 'air') return sol
  if (p.sePose && auRepos(etat)) return sol + ALTITUDE_REPOS[kind]
  return sol + ALTITUDE_VOL[kind]
}

/** Etat de vie ordinaire d'une espece, celui ou elle revient toujours. */
function etatOrdinaire(p: ProfilAnimal): EtatAnimal {
  if (p.milieu === 'eau' || p.milieu === 'surface') return 'nage'
  if (p.demarche === 'vol_plane') return 'plane'
  if (p.social === 'troupeau' || p.social === 'banc') return 'troupeau'
  return 'errance'
}

// ------------------------------------------------------------------- generation

/**
 * Peuple le monde. Les especes sociales naissent en GROUPES: un individu est
 * pose, ses congeneres autour de lui. Sans cela un troupeau de cerfs mettait
 * cinq minutes a se former en traversant la carte.
 */
export function genererAnimaux(terrain: ITerrain, seed: number, especes: readonly AnimalKind[]): Animal[] {
  const alea = new Alea((seed ^ 0x00a11ec7) >>> 0)
  const animaux: Animal[] = []

  for (const kind of especes) {
    const def = ANIMAUX[kind]
    const p = PROFILS[kind]
    const quota = POPULATION[kind]
    const parGroupe = p.social === 'aucun' ? 1 : p.social === 'groupe' ? 3 : 5
    const rayonGroupe = Math.max(3, p.cohesion * 0.45)
    let places = 0
    const essaisMax = quota * 90

    for (let essai = 0; essai < essaisMax && places < quota; essai++) {
      // Tous les tirages ont lieu avant les rejets: le flux d'aleatoire ne
      // depend donc pas du resultat du test de terrain, seulement de la graine.
      const cx = alea.entre(MARGE_MONDE + 2, WORLD_SIZE - MARGE_MONDE - 2)
      const cz = alea.entre(MARGE_MONDE + 2, WORLD_SIZE - MARGE_MONDE - 2)
      if (!def.biomes.includes(terrain.biome(cx, cz))) continue
      if (!placeValide(terrain, kind, cx, cz)) continue

      for (let m = 0; m < parGroupe && places < quota; m++) {
        // Trois essais par membre: dans une baie etroite ou sur une crete, un
        // seul tirage autour du chef laisse des groupes a moitie vides.
        for (let bis = 0; bis < 3; bis++) {
          const ang = alea.entre(0, Math.PI * 2)
          const r = m === 0 ? 0 : alea.entre(1.2, rayonGroupe)
          const couleur = alea.choix(def.couleurs)
          const taille = def.taille * alea.entre(0.88, 1.12)
          const phase = alea.entre(0, Math.PI * 2)
          const rot = alea.entre(0, Math.PI * 2)
          const minuterie = alea.entre(0.5, 5)
          const x = cx + Math.cos(ang) * r
          const z = cz + Math.sin(ang) * r
          if (!def.biomes.includes(terrain.biome(x, z))) continue
          if (!placeValide(terrain, kind, x, z)) continue

          const etat = etatOrdinaire(p)
          animaux.push({
            id: animaux.length,
            kind,
            pos: { x, z },
            y: yAnimal(terrain, kind, etat, x, z),
            rot,
            etat,
            cible: null,
            minuterie,
            esquive: 0,
            compagnon: -1,
            couleur,
            taille,
            phase,
            repas: 0,
            dernierRepas: 0,
            ami: null,
            maison: null,
            ancre: null,
            rayon: 0,
          })
          places++
          break
        }
      }
    }
  }

  peuplerIle2(terrain, seed, especes, animaux)
  return animaux
}

/**
 * LA FAUNE DE L'ILE 2 — une passe a part, appendue APRES celle du monde 1.
 *
 * C'est le meme invariant que les noeuds de ressource, et pour la meme raison:
 * `animal_familier` a pour clef `animal_id`, qui est un index d'insertion.
 * Semer l'ile 2 avant, ou intercaler ses especes dans la boucle du monde 1,
 * rendrait a chaque enfant une autre bete que celle qu'il a apprivoisee — et
 * decalerait au passage tout le flux d'aleatoire de l'ile 1.
 *
 * Elle a sa PROPRE graine derivee. Partager celle du monde 1 aurait marche
 * aussi, mais alors le jour ou l'on retouche un quota de l'ile 2, toute la
 * faune du monde 1 changerait de place: on ne se laisse pas ce piege.
 *
 * Elle ne fait rien du tout si le terrain ne connait pas les regions — un
 * `Terrain` seul, un faux terrain de test. C'est ce qui rend cette passe
 * invisible pour tout ce qui existait avant, et c'est aussi ce qui donne au
 * test d'identite son oracle permanent.
 */
function peuplerIle2(
  terrain: ITerrain,
  seed: number,
  especes: readonly AnimalKind[],
  animaux: Animal[],
): void {
  const lecteur = terrain as { regionAuPoint?: (x: number, z: number) => unknown }
  if (typeof lecteur.regionAuPoint !== 'function') return

  const alea = new Alea((seed ^ 0x1_1e02_fa) >>> 0)
  const minX = ILE2_X0 + MARGE_MONDE + 2
  const maxX = ILE2_X0 + ILE2_COTE - MARGE_MONDE - 2
  const minZ = ILE2_Z0 + MARGE_MONDE + 2
  const maxZ = ILE2_Z0 + ILE2_COTE - MARGE_MONDE - 2

  for (const kind of especes) {
    const def = ANIMAUX[kind]
    const p = PROFILS[kind]
    const quota = POPULATION_ILE2[kind]
    if (quota <= 0) continue
    const parGroupe = p.social === 'aucun' ? 1 : p.social === 'groupe' ? 3 : 5
    const rayonGroupe = Math.max(3, p.cohesion * 0.45)
    let places = 0
    const essaisMax = quota * 90

    for (let essai = 0; essai < essaisMax && places < quota; essai++) {
      // Tous les tirages avant tout rejet, comme partout ailleurs.
      const cx = alea.entre(minX, maxX)
      const cz = alea.entre(minZ, maxZ)
      if (!def.biomes.includes(terrain.biome(cx, cz))) continue
      if (!placeValide(terrain, kind, cx, cz)) continue

      for (let m = 0; m < parGroupe && places < quota; m++) {
        for (let bis = 0; bis < 3; bis++) {
          const ang = alea.entre(0, Math.PI * 2)
          const r = m === 0 ? 0 : alea.entre(1.2, rayonGroupe)
          const couleur = alea.choix(def.couleurs)
          const taille = def.taille * alea.entre(0.88, 1.12)
          const phase = alea.entre(0, Math.PI * 2)
          const rot = alea.entre(0, Math.PI * 2)
          const minuterie = alea.entre(0.5, 5)
          const x = cx + Math.cos(ang) * r
          const z = cz + Math.sin(ang) * r
          if (!def.biomes.includes(terrain.biome(x, z))) continue
          if (!placeValide(terrain, kind, x, z)) continue

          const etat = etatOrdinaire(p)
          animaux.push({
            id: animaux.length,
            kind,
            pos: { x, z },
            y: yAnimal(terrain, kind, etat, x, z),
            rot,
            etat,
            cible: null,
            minuterie,
            esquive: 0,
            compagnon: -1,
            couleur,
            taille,
            phase,
            repas: 0,
            dernierRepas: 0,
            ami: null,
            maison: null,
            ancre: null,
            rayon: 0,
          })
          places++
          break
        }
      }
    }
  }
}

// ---------------------------------------------------------------- deplacement

/** Ramene un angle dans [-PI, PI]. */
function normaliserAngle(a: number): number {
  let r = a % (Math.PI * 2)
  if (r > Math.PI) r -= Math.PI * 2
  else if (r < -Math.PI) r += Math.PI * 2
  return r
}

/**
 * Fait pivoter l'animal VERS le cap (capx, capz), sans jamais depasser la
 * vitesse angulaire de son espece.
 *
 * Le cap voulu et le cap affiche sont deux choses distinctes: le deplacement va
 * toujours ou il doit aller, c'est l'orientation qui met le temps qu'il faut a
 * suivre. Affecter `rot` directement, comme on le faisait, laissait un dauphin
 * se retourner de 180 degres en 50 ms, deux cents fois par minute.
 */
function orienterVers(a: Animal, capx: number, capz: number, dt: number): void {
  if (Math.abs(capx) < 1e-9 && Math.abs(capz) < 1e-9) return
  const max = PROFILS[a.kind].virage * dt
  const ecart = normaliserAngle(Math.atan2(capx, capz) - a.rot)
  a.rot = normaliserAngle(a.rot + (ecart > max ? max : ecart < -max ? -max : ecart))
}

/**
 * Tente d'avancer dans la direction (dirx, dirz). Si c'est bloque (eau pour un
 * terrestre, terre pour un poisson, falaise, bord du monde), essaie de longer en
 * deviant l'angle. Rend vrai si l'animal a bouge.
 *
 * Le contournement a une memoire: l'ecart deja tenu est relache doucement puis
 * rouvert du MEME cote. Un animal qui longe une frontiere continue donc de la
 * longer, au lieu de retester « tout droit » a chaque tick et d'osciller.
 */
function avancer(
  a: Animal,
  dirx: number,
  dirz: number,
  vitesse: number,
  dt: number,
  terrain: ITerrain,
): boolean {
  const norme = Math.hypot(dirx, dirz)
  if (norme < 1e-6) return false
  const ux = dirx / norme
  const uz = dirz / norme
  const pas = vitesse * dt
  if (pas <= 0) return false

  const relache = RELACHE_ESQUIVE * dt
  let tenu = a.esquive
  if (tenu > relache) tenu -= relache
  else if (tenu < -relache) tenu += relache
  else tenu = 0
  const cote = a.esquive < 0 ? -1 : 1

  // Ordre d'essai: RELACHER l'esquive en cours, sinon la TENIR telle quelle,
  // sinon l'ouvrir davantage du meme cote; l'autre cote en dernier recours.
  //
  // Le cran « tenir » est ce qui evite le battement: sans lui, l'animal relache
  // son ecart, se cogne, en reprend un cran, relache a nouveau... et accumulait
  // deux fois RELACHE_ESQUIVE de rotation par seconde tout le long d'une cote.
  for (let i = 0; i <= ECARTS.length * 2; i++) {
    let ang: number
    if (i === 0) ang = tenu
    else if (i <= ECARTS.length) ang = a.esquive + cote * (ECARTS[i - 1] as number)
    else ang = -cote * (ECARTS[i - 1 - ECARTS.length] as number)
    const c = Math.cos(ang)
    const s = Math.sin(ang)
    const nx = ux * c - uz * s
    const nz = ux * s + uz * c
    const px = a.pos.x + nx * pas
    const pz = a.pos.z + nz * pas
    if (!placeValide(terrain, a.kind, px, pz)) continue
    a.pos.x = px
    a.pos.z = pz
    a.y = yAnimal(terrain, a.kind, a.etat, px, pz)
    a.esquive = normaliserAngle(ang)
    orienterVers(a, nx, nz, dt)
    return true
  }
  return false
}

/**
 * Vrai si l'animal peut esperer aller de sa position jusqu'a (x, z) tout droit.
 */
function cheminLibre(a: Animal, terrain: ITerrain, x: number, z: number): boolean {
  for (let k = 1; k <= 3; k++) {
    const t = k * 0.25
    if (!placeValide(terrain, a.kind, a.pos.x + (x - a.pos.x) * t, a.pos.z + (z - a.pos.z) * t)) return false
  }
  return true
}

/** Rayon d'arrivee reel: jamais plus petit que la foulee du tick. */
function rayonArrivee(vitesse: number, dt: number): number {
  const pas = vitesse * dt
  return pas > ARRIVE ? pas : ARRIVE
}

/** Pose ou deplace la cible d'un animal, sans allouer si elle existe deja. */
function poserCible(a: Animal, x: number, z: number): void {
  if (a.cible) {
    a.cible.x = x
    a.cible.z = z
  } else {
    a.cible = { x, z }
  }
}

/**
 * Choisit une cible d'errance dans la portee propre a l'espece. `biaisX/biaisZ`
 * attire le tirage vers un point (le groupe, en general): c'est ce qui fait
 * qu'une bande de mouettes reste une bande sans se coller.
 *
 * Le tirage part du cap ACTUEL et ne s'en ecarte que d'un cone, qui s'ouvre a
 * chaque essai rate pour ne jamais enfermer une bete dans un cul-de-sac. Un
 * tirage uniforme sur le tour complet faisait faire demi-tour a l'animal a
 * chaque etape: la moitie des directions tirees etaient derriere lui, et c'est
 * beaucoup de tournis pour un animal qui se promene.
 */
function nouvelleCible(
  a: Animal,
  p: ProfilAnimal,
  terrain: ITerrain,
  alea: Alea,
  biaisX: number,
  biaisZ: number,
  poidsBiais: number,
): void {
  const dmin = p.errance[0]
  const dmax = p.errance[1]
  // `rot` est un cap (x = sin, z = cos); ici l'angle sert a (cos, sin) d'ou le
  // quart de tour.
  const cap = Math.PI / 2 - a.rot
  for (let essai = 0; essai < 6; essai++) {
    const ouverture = CONE_ERRANCE + essai * 0.6
    const ang = cap + alea.entre(-ouverture, ouverture)
    // L'etape se raccourcit a chaque essai rate: une baie profonde peut etre
    // plus petite que la portee d'errance de l'espece, et un dauphin qui vise
    // toujours vingt metres dans une poche qui en fait dix passe sa vie a
    // raboter le fond.
    const d = alea.entre(dmin, dmax) * (1 - essai * 0.15)
    let x = a.pos.x + Math.cos(ang) * d
    let z = a.pos.z + Math.sin(ang) * d
    if (poidsBiais > 0) {
      x += (biaisX - x) * poidsBiais
      z += (biaisZ - z) * poidsBiais
    }
    if (!placeValide(terrain, a.kind, x, z)) continue
    // Le CHEMIN doit tenir, pas seulement le point d'arrivee: on l'echantillonne
    // au quart, a la moitie et aux trois quarts. Sans cela l'animal visait
    // l'autre rive d'une pointe de terre, se cognait pendant vingt secondes et
    // rabotait la cote de travers au lieu d'aller quelque part.
    if (!cheminLibre(a, terrain, x, z)) continue
    poserCible(a, x, z)
    return
  }
  a.cible = null
}

/**
 * Facteur de vitesse instantane, pour les demarches qui ne sont pas continues:
 * le scorpion part en salves, la tortue avance par a-coups. La moyenne reste
 * egale a un, la vitesse annoncee dans la table est donc respectee.
 */
function saccade(p: ProfilAnimal, phase: number): number {
  if (p.demarche === 'saccade') return (phase * 0.9) % 1 < 0.45 ? 2.2 : 0
  if (p.demarche === 'traine') return (phase * 0.55) % 1 < 0.6 ? 1.65 : 0
  return 1
}

// ------------------------------------------------------------- vie de groupe

/**
 * Resultat du dernier balayage de voisinage. Des variables de module plutot
 * qu'un objet rendu: `majAnimaux` ne doit rien allouer.
 */
let grNb = 0
let grPoids = 0
let grBaryX = 0
let grBaryZ = 0
let grProcheD2 = 0
let grEcartX = 0
let grEcartZ = 0
let grAlarme = false
let grAlarmeX = 0
let grAlarmeZ = 0

/** Distance en deca de laquelle la repulsion cesse de croitre, en metres. */
const COLLE = 0.35

/**
 * Balaye les congeneres a portee: barycentre, distance du plus proche, sens de
 * l'ecart, alarme.
 *
 * L'ecart est la SOMME des repulsions de tous les voisins, ponderee par
 * l'inverse du carre de la distance, et non la direction du seul plus proche.
 * Dans un banc serre, le plus proche voisin change a chaque tick et l'animal se
 * retournait a chaque fois; la somme, elle, tourne doucement.
 *
 * Le barycentre est pondere de la meme facon: un voisin compte pour rien au
 * bord du rayon de cohesion et pour un tout pres. Un comptage franc faisait
 * sauter le centre du groupe des qu'un congenere entrait dans le cercle ou en
 * sortait, et tout le troupeau se reorientait d'un coup.
 */
function scannerGroupe(animaux: Animal[], indice: number, rayon: number): void {
  const moi = animaux[indice] as Animal
  const r2 = rayon * rayon
  grNb = 0
  grPoids = 0
  grBaryX = 0
  grBaryZ = 0
  grProcheD2 = Infinity
  grEcartX = 0
  grEcartZ = 0
  grAlarme = false
  grAlarmeX = 0
  grAlarmeZ = 0
  bornes(moi.pos.x, moi.pos.z, rayon)
  for (let cz = bz0; cz <= bz1; cz++) {
    const base = cz * COTE_X
    for (let cx = bx0; cx <= bx1; cx++) {
      const c = base + cx
      const fin = grDebut[c + 1] as number
      for (let k = grDebut[c] as number; k < fin; k++) {
        const j = grOrdre[k] as number
        if (j === indice) continue
        const b = animaux[j] as Animal
        if (b.kind !== moi.kind) continue
        const d2 = dist2(moi.pos.x, moi.pos.z, b.pos.x, b.pos.z)
        if (d2 > r2) continue
        grNb++
        const poidsBary = 1 - Math.sqrt(d2) / rayon
        grPoids += poidsBary
        grBaryX += b.pos.x * poidsBary
        grBaryZ += b.pos.z * poidsBary
        if (d2 < grProcheD2) grProcheD2 = d2
        const poids = 1 / (d2 > COLLE * COLLE ? d2 : COLLE * COLLE)
        grEcartX += (moi.pos.x - b.pos.x) * poids
        grEcartZ += (moi.pos.z - b.pos.z) * poids
        if (!grAlarme && b.etat === 'fuite') {
          grAlarme = true
          if (b.cible) {
            grAlarmeX = b.cible.x
            grAlarmeZ = b.cible.z
          } else {
            // Le voisin fuit le joueur: on ne sait pas d'ou, on le suit lui.
            grAlarmeX = b.pos.x + (b.pos.x - moi.pos.x) * 3
            grAlarmeZ = b.pos.z + (b.pos.z - moi.pos.z) * 3
          }
        }
      }
    }
  }
  if (grPoids > 1e-9) {
    grBaryX /= grPoids
    grBaryZ /= grPoids
    const n = Math.hypot(grEcartX, grEcartZ)
    if (n > 1e-9) {
      grEcartX /= n
      grEcartZ /= n
    }
  }
}

/**
 * Poids de l'appel du groupe face a la repulsion des voisins.
 *
 * Les deux tiraient a tour de role sur des ticks differents — un pas dehors, un
 * pas dedans — et l'animal faisait la navette. Ils tirent maintenant ENSEMBLE,
 * dans un seul vecteur; en dessous de un, la repulsion l'emporte de justesse et
 * la resultante ne s'annule jamais.
 */
const POIDS_COHESION = 0.75

/**
 * Poids de la correction du banc face a la direction du but, pendant la nage.
 *
 * En dessous de un, la vie de groupe INFLECHIT la route sans la commander. Au
 * dessus, elle la commandait: deux dauphins qui se frolaient se renvoyaient l'un
 * l'autre a chaque tick et remontaient leur cap de cent degres a chaque fois.
 */
const POIDS_GROUPE = 0.25

/** En deca de cette distance, le barycentre du groupe ne donne plus de cap. */
const REGARD_GROUPE = 3.5

/**
 * Part de la vitesse de rotation utilisee pour se tourner vers le groupe quand
 * on ne bouge pas. C'est un COUP D'OEIL, pas une manoeuvre: a pleine vitesse,
 * une bete a l'arret au milieu de son troupeau passait son temps a pivoter
 * derriere un barycentre qui gigote.
 */
const REGARD_LENT = 0.3

/** Resultat de `pousseeSociale`: direction a suivre, et force de 0 a 1. */
let pousseeX = 0
let pousseeZ = 0
let pousseeUrgence = 0
let pousseeBesoin = 0

/**
 * Combine « garder ses distances » et « rester avec les siens » en UNE
 * direction. Rend faux si le groupe est bien place et qu'il n'y a rien a
 * corriger. A n'appeler qu'apres `scannerGroupe`, et seulement si `grNb > 0`.
 *
 * Les deux forces sont CONTINUES: elles s'annulent en douceur au seuil au lieu
 * de s'allumer et de s'eteindre. En tout ou rien, un poisson de banc basculait
 * vingt-cinq fois par minute entre « je m'ecarte » et « je vais a ma cible »,
 * deux caps souvent opposes — a lui seul, c'etait l'essentiel du tournis des
 * especes aquatiques.
 */
function pousseeSociale(a: Animal, separation: number, portee: number, besoinMin: number): boolean {
  let poidsEcart = (separation - Math.sqrt(grProcheD2)) / separation
  if (poidsEcart < 0) poidsEcart = 0
  const dBary = dist(a.pos.x, a.pos.z, grBaryX, grBaryZ)
  let poidsBary = portee > 1e-6 ? (dBary - portee) / portee : 0
  if (poidsBary < 0) poidsBary = 0
  else if (poidsBary > 1) poidsBary = 1
  // L'appel du groupe s'eteint avec le poids total des voisins. La moyenne est
  // NORMALISEE: un unique congenere tout au bord du rayon de cohesion, qui ne
  // devrait presque pas compter, devenait a lui seul le centre du groupe et
  // tirait dessus de toutes ses forces jusqu'a disparaitre d'un coup.
  if (grPoids < 1) poidsBary *= grPoids
  pousseeUrgence = poidsBary
  const besoinEcart = poidsEcart * separation
  const besoinBary = dBary - portee
  pousseeBesoin = besoinEcart > besoinBary ? besoinEcart : besoinBary
  if (pousseeBesoin < besoinMin) return false
  // La consigne monte en douceur au sortir de la zone morte. Un rappel qui
  // s'allume d'un coup, meme faible, fait sauter le cap de vingt degres a
  // chaque fois qu'on franchit le seuil — et on le franchit sans arret.
  let rampe = (pousseeBesoin - besoinMin) / besoinMin
  if (rampe > 1) rampe = 1
  pousseeX = grEcartX * poidsEcart
  pousseeZ = grEcartZ * poidsEcart
  if (poidsBary > 0 && dBary > 1e-6) {
    pousseeX += ((grBaryX - a.pos.x) / dBary) * poidsBary * POIDS_COHESION
    pousseeZ += ((grBaryZ - a.pos.z) / dBary) * poidsBary * POIDS_COHESION
  }
  pousseeX *= rampe
  pousseeZ *= rampe
  return true
}

// -------------------------------------------------------------- jeu entre betes

/** Vrai si deux especes peuvent se rencontrer: on ne joue pas a travers l'eau. */
function memeMilieu(a: AnimalKind, b: AnimalKind): boolean {
  const ma = PROFILS[a].milieu
  const mb = PROFILS[b].milieu
  if (ma === mb) return true
  // Le canard flotte et le poisson nage: ils sont dans la meme eau.
  const eau = (m: string): boolean => m === 'eau' || m === 'surface'
  return eau(ma) && eau(mb)
}

/** Vrai si cet animal est disponible pour jouer maintenant. */
function disponible(b: Animal): boolean {
  if (b.etat === 'dort' || b.etat === 'boule' || b.etat === 'fuite' || b.etat === 'suit') return false
  // Un planeur ne descend pas jouer: il est a dix-sept metres du sol.
  return PROFILS[b.kind].demarche !== 'vol_plane'
}

/**
 * Indice du compagnon de jeu le plus proche, de n'importe quelle espece, ou -1.
 * C'est le seul balayage un peu couteux, et il ne concerne qu'une poignee
 * d'individus a la fois: ceux qui viennent d'arriver et ceux qui jouent.
 */
function compagnonLePlusProche(animaux: Animal[], indice: number, rayon: number): number {
  const moi = animaux[indice] as Animal
  let meilleur = -1
  let meilleurD2 = rayon * rayon
  bornes(moi.pos.x, moi.pos.z, rayon)
  for (let cz = bz0; cz <= bz1; cz++) {
    const base = cz * COTE_X
    for (let cx = bx0; cx <= bx1; cx++) {
      const c = base + cx
      const fin = grDebut[c + 1] as number
      for (let k = grDebut[c] as number; k < fin; k++) {
        const j = grOrdre[k] as number
        if (j === indice) continue
        const b = animaux[j] as Animal
        const d2 = dist2(moi.pos.x, moi.pos.z, b.pos.x, b.pos.z)
        if (d2 >= meilleurD2) continue
        if (!memeMilieu(moi.kind, b.kind)) continue
        if (!disponible(b)) continue
        meilleurD2 = d2
        meilleur = j
      }
    }
  }
  return meilleur
}

/** Invite un compagnon a jouer. Il accepte s'il est curieux et disponible. */
function inviter(b: Animal, a: Animal, duree: number): void {
  if (PROFILS[b.kind].curiosite <= 0) return
  if (!disponible(b)) return
  if (b.etat === 'troupeau' && b.cible !== null) return
  b.etat = 'troupeau'
  b.minuterie = duree
  b.compagnon = a.id
  poserCible(b, a.pos.x, a.pos.z)
}

/**
 * Le compagnon de jeu memorise, s'il est toujours la, toujours disponible et
 * toujours a portee. Sinon -1: la partie est finie.
 */
function compagnonTenu(animaux: Animal[], a: Animal, portee: number): number {
  const j = a.compagnon
  if (j < 0 || j >= animaux.length) return -1
  const b = animaux[j] as Animal
  // Les identifiants sont les indices de creation; on le verifie quand meme,
  // parce qu'un monde recharge pourrait ne pas respecter cet ordre.
  if (b.id !== j || !disponible(b) || !memeMilieu(a.kind, b.kind)) return -1
  return dist2(a.pos.x, a.pos.z, b.pos.x, b.pos.z) <= portee * portee ? j : -1
}

/**
 * Tourne autour d'un compagnon sans jamais le rattraper: on accourt s'il est
 * loin, puis on decrit un cercle autour de lui, dans un sens propre a chacun.
 *
 * Le rayon du cercle s'ouvre avec la vitesse et se ferme avec l'agilite: un
 * dauphin lance a quatre metres par seconde ne pivote pas sur place autour d'un
 * congenere, il decrit une large boucle. Un cercle serre a pleine vitesse, c'est
 * une toupie, pas un jeu.
 */
function jouerAutour(
  a: Animal,
  px: number,
  pz: number,
  vitesse: number,
  p: ProfilAnimal,
  dt: number,
  terrain: ITerrain,
): void {
  const vJeu = vitesse * (1 + p.entrain * 0.6)
  // Les deux partenaires tournent DU MEME COTE (celui du plus ancien des deux):
  // ils se poursuivent autour d'un centre commun, et la paire tourne donc deux
  // fois plus vite que chacun d'eux — d'ou le facteur deux. A sens contraires,
  // la paire ne tournait pas du tout: les deux betes filaient cote a cote.
  //
  // Le cercle s'OUVRE avec la vitesse au lieu de la brider: une bete rapide
  // decrit une large boucle. Un cercle serre a pleine vitesse, c'est une toupie;
  // un cercle serre a vitesse bridee, c'est une bete qui n'avance plus.
  const rayon = Math.max(1.3 + a.taille * 1.1, (2 * vJeu) / Math.min(OMEGA_JEU, p.virage))
  const dx = a.pos.x - px
  const dz = a.pos.z - pz
  const dd = Math.hypot(dx, dz)
  if (dd < 1e-3) {
    avancer(a, 1, 0, vitesse, dt, terrain)
    return
  }
  const ux = dx / dd
  const uz = dz / dd
  if (dd > rayon * 2.4) {
    // Il est encore loin: on le rejoint au galop.
    if (!avancer(a, -ux, -uz, vitesse * 1.6, dt, terrain)) a.minuterie = Math.min(a.minuterie, 0.2)
    return
  }
  // Tangente, plus une correction douce pour tenir le rayon du cercle.
  const aine = a.compagnon >= 0 && a.compagnon < a.id ? a.compagnon : a.id
  const sens = (aine & 1) === 0 ? 1 : -1
  const corr = (dd - rayon) * 0.9
  const dirx = -uz * sens - ux * corr
  const dirz = ux * sens - uz * corr
  if (!avancer(a, dirx, dirz, vJeu, dt, terrain)) {
    a.minuterie = Math.min(a.minuterie, 0.2)
  }
}

// ------------------------------------------------------------------- foyer

/**
 * Tire une cible de promenade DANS le foyer.
 *
 * La racine carree du tirage donne une repartition uniforme sur le disque: sans
 * elle, l'animal passait sa vie collee a son panier, parce qu'un tirage lineaire
 * du rayon concentre les points au centre.
 */
function cibleDansFoyer(a: Animal, alea: Alea, terrain: ITerrain): void {
  const ancre = a.ancre as Vec2
  for (let essai = 0; essai < 8; essai++) {
    const ang = alea.entre(0, Math.PI * 2)
    const r = Math.sqrt(alea.reel()) * a.rayon
    const x = ancre.x + Math.cos(ang) * r
    const z = ancre.z + Math.sin(ang) * r
    if (!placeValide(terrain, a.kind, x, z)) continue
    poserCible(a, x, z)
    return
  }
  poserCible(a, ancre.x, ancre.z)
}

/**
 * Vie d'un animal qui a un foyer: adopte dans une maison, ou installe dans un
 * enclos.
 *
 * Il ne fuit plus, il ne part plus en troupeau, il ne joue plus a l'autre bout
 * de la carte: il deambule chez lui, il vient accueillir celui qui rentre, et la
 * nuit il rejoint son coin pour dormir. C'est volontairement une branche a part,
 * traitee avant tout le reste: ces animaux se comptent sur les doigts d'une
 * main, et la vie sauvage des trois cents autres ne doit pas payer un centime
 * pour eux.
 */
function majFoyer(
  a: Animal,
  def: AnimalDef,
  p: ProfilAnimal,
  dt: number,
  joueurPos: Vec2,
  d: number,
  terrain: ITerrain,
  h: Horloge,
  alea: Alea,
): void {
  const ancre = a.ancre as Vec2
  const dAncre = dist(a.pos.x, a.pos.z, ancre.x, ancre.z)
  const sommeil = p.nocturne ? !h.nuit : def.dortLaNuit && h.nuit

  if (sommeil) {
    // Il rentre se coucher dans son coin, et il y reste.
    if (dAncre > 0.7) {
      a.etat = 'errance'
      avancer(a, ancre.x - a.pos.x, ancre.z - a.pos.z, def.vitesse, dt, terrain)
    } else {
      a.etat = 'dort'
      a.cible = null
      a.y = yAnimal(terrain, a.kind, a.etat, a.pos.x, a.pos.z)
    }
    return
  }

  // L'accueil: quelqu'un arrive chez lui, il va a sa rencontre. On verifie que
  // le JOUEUR est bien du cote du foyer, sinon un animal de compagnie suivrait
  // son maitre au bout du jardin en abandonnant la maison.
  const dJoueurAncre = dist(joueurPos.x, joueurPos.z, ancre.x, ancre.z)
  if (d < PORTEE_ACCUEIL && dJoueurAncre < a.rayon + PORTEE_ACCUEIL) {
    a.etat = 'suit'
    a.cible = null
    const vx = joueurPos.x - a.pos.x
    const vz = joueurPos.z - a.pos.z
    if (d > DISTANCE_SUIVI) avancer(a, vx, vz, def.vitesse, dt, terrain)
    else orienterVers(a, vx, vz, dt)
    return
  }

  a.minuterie -= dt

  if (dAncre > a.rayon) {
    // Trop loin de chez lui (un coup de vent, une pose de batiment): il rentre.
    a.etat = 'errance'
    poserCible(a, ancre.x, ancre.z)
  } else if (a.etat === 'broute' || a.etat === 'dort') {
    if (a.minuterie > 0) {
      a.y = yAnimal(terrain, a.kind, a.etat, a.pos.x, a.pos.z)
      return
    }
    a.etat = 'errance'
    a.cible = null
  } else if (a.cible === null) {
    a.etat = 'errance'
    cibleDansFoyer(a, alea, terrain)
  }

  const cible = a.cible
  if (cible === null) return
  const v = def.vitesse * saccade(p, a.phase)
  const arrive = rayonArrivee(v, dt)
  if (dist2(a.pos.x, a.pos.z, cible.x, cible.z) <= arrive * arrive) {
    a.cible = null
    a.etat = 'broute'
    a.minuterie = alea.entre(2, 6)
    return
  }
  if (v > 0 && !avancer(a, cible.x - a.pos.x, cible.z - a.pos.z, v, dt, terrain)) a.cible = null
}

// ----------------------------------------------------------------- simulation

/**
 * Un joueur, du point de vue de la faune: ou il est, et ce qu'il tend au bout
 * de sa main. C'est tout ce dont un animal a besoin de savoir d'un humain.
 */
export interface JoueurFaune {
  pos: Vec2
  /** Friandise tenue en main, ou null. Elle attire les especes qui l'aiment. */
  appat: ItemId | null
  /**
   * Jeton du joueur, compare a `Animal.ami`. Un familier ne suit QUE celui qui
   * l'a nourri: sans ce champ, il suivait le plus proche, si bien que la
   * luciole d'un autre se collait aux pieds d'un inconnu et lui volait chaque
   * tap. Absent (ancien appelant), le joueur n'est l'ami de personne.
   */
  jeton?: string | null
}

/**
 * Position du joueur le plus proche, reutilisee d'un animal a l'autre: la
 * boucle chaude ne doit rien allouer, meme avec trois joueurs sur la carte.
 */
const proche: Vec2 = { x: 0, z: 0 }

/**
 * Simulation de la faune pour PLUSIEURS joueurs a la fois.
 *
 * Un animal ne regarde qu'un humain: le PLUS PROCHE. C'est lui qu'il fuit,
 * c'est de lui que depend la cadence de mise a jour, et c'est sa friandise qui
 * l'attire. Un troisieme joueur a l'autre bout de la carte ne doit rien couter
 * de plus qu'une comparaison de distances.
 *
 * Une liste VIDE (personne de connecte) ne fait rien du tout: sur le serveur,
 * la faune s'arrete quand plus personne ne la regarde, et ne coute alors rien.
 */
export function majAnimauxMulti(
  animaux: Animal[],
  dtTick: number,
  joueurs: readonly JoueurFaune[],
  terrain: ITerrain,
  h: Horloge,
  alea: Alea,
): void {
  if (joueurs.length === 0) return
  construireGrille(animaux)
  const fine2 = PORTEE_FINE * PORTEE_FINE
  const moyenne2 = PORTEE_MOYENNE * PORTEE_MOYENNE

  for (let i = 0; i < animaux.length; i++) {
    const a = animaux[i] as Animal
    const def = ANIMAUX[a.kind]
    const p = PROFILS[a.kind]
    let dx = 0
    let dz = 0
    let appat: ItemId | null = null
    let meilleur = Infinity
    for (let k = 0; k < joueurs.length; k++) {
      const j = joueurs[k] as JoueurFaune
      const ex = a.pos.x - j.pos.x
      const ez = a.pos.z - j.pos.z
      const d2 = ex * ex + ez * ez
      if (d2 >= meilleur) continue
      meilleur = d2
      dx = ex
      dz = ez
      appat = j.appat
      proche.x = j.pos.x
      proche.z = j.pos.z
    }
    // UN FAMILIER NE CONNAIT QUE SON AMI. S'il est connecte, c'est lui que
    // l'animal regarde, ou qu'il soit; sinon personne ne le mene (`etranger`):
    // il ne fuit pas — il est familier — mais il ne suit pas un inconnu.
    let etranger = false
    if (a.ami !== null && a.repas >= REPAS_APPRIVOISEMENT) {
      etranger = true
      for (let k = 0; k < joueurs.length; k++) {
        const j = joueurs[k] as JoueurFaune
        if (j.jeton !== a.ami) continue
        etranger = false
        dx = a.pos.x - j.pos.x
        dz = a.pos.z - j.pos.z
        appat = j.appat
        proche.x = j.pos.x
        proche.z = j.pos.z
        break
      }
      if (etranger) appat = null
    }
    // Tout ce qui suit ne connait qu'UN humain, celui qu'on vient d'elire: le
    // reste de la fonction n'a pas eu a changer d'un caractere.
    const joueurPos = proche

    // La phase avance toujours (le rendu s'en sert); l'IA, elle, suit la cadence
    // degradee avec la distance.
    a.phase += dtTick
    const eloignement = dx * dx + dz * dz
    let dt = dtTick
    if (eloignement > fine2) {
      const sous = eloignement > moyenne2 ? 8 : 4
      if (((a.phase / dtTick) | 0) % sous !== 0) continue
      dt = dtTick * sous
    }

    const d = Math.hypot(dx, dz)

    // Un animal qui a un foyer vit chez lui: c'est une autre vie, elle est
    // ailleurs. Un seul test pour les trois cents autres.
    if (a.ancre !== null) {
      majFoyer(a, def, p, dt, joueurPos, d, terrain, h, alea)
      continue
    }

    // Un nocturne dort le JOUR; tous les autres dorment la nuit s'ils dorment.
    const sommeil = p.nocturne ? !h.nuit : def.dortLaNuit && h.nuit
    const familier = a.repas >= REPAS_APPRIVOISEMENT
    const attire = appat !== null && def.friandises.includes(appat)
    const social = p.social !== 'aucun' && p.cohesion > 0

    // -------- vie de groupe: barycentre, voisin le plus proche, alarme
    if (social) {
      scannerGroupe(animaux, i, p.cohesion)
    } else {
      grNb = 0
      grAlarme = false
    }

    // -------- choix de l'etat
    //
    // Un familier n'a pas besoin d'appat: il vient parce qu'il te connait. C'est
    // ainsi qu'on le mene jusqu'a son enclos ou jusqu'a la maison.
    if (etranger && a.etat === 'suit') {
      a.etat = sommeil ? 'dort' : etatOrdinaire(p)
      a.cible = null
    } else if (etranger) {
      // Rien a choisir ici: ni suivre un inconnu, ni le fuir. La suite
      // (sommeil, errance) le traite comme une bete calme.
      if (sommeil && a.etat !== 'dort') {
        a.etat = 'dort'
        a.cible = null
      } else if (!sommeil && a.etat === 'dort') {
        a.etat = etatOrdinaire(p)
        a.cible = null
      }
    } else if ((attire && d < PORTEE_APPAT) || (familier && !sommeil && d < PORTEE_FAMILIER)) {
      a.etat = 'suit'
      a.minuterie = Math.max(a.minuterie, 1)
    } else if (a.etat === 'suit') {
      a.minuterie -= dt
      if (a.minuterie <= 0 || d > (familier ? PORTEE_ABANDON_FAMILIER : PORTEE_ABANDON)) {
        a.etat = sommeil ? 'dort' : etatOrdinaire(p)
        a.cible = null
      }
    } else if (sommeil) {
      if (a.etat !== 'dort') {
        a.etat = 'dort'
        a.cible = null
      }
    } else if (a.etat === 'dort') {
      a.etat = etatOrdinaire(p)
      a.cible = null
    } else if (d < def.distanceFuite && !familier) {
      // Le joueur est trop pres: chaque espece a sa reaction. Un familier, lui,
      // ne s'ecarte plus: c'est tout le sens de l'avoir apprivoise.
      switch (p.reaction) {
        case 'fuite':
          if (a.etat !== 'fuite') {
            a.etat = 'fuite'
            a.minuterie = alea.entre(1.5, 3)
            a.cible = null
          }
          break
        case 'boule':
          a.etat = 'boule'
          a.minuterie = Math.max(a.minuterie, 1.5)
          a.cible = null
          break
        case 'garde':
          a.etat = 'garde'
          a.minuterie = Math.max(a.minuterie, 1.5)
          a.cible = null
          break
        case 'indifferent':
          break
      }
    } else if (social && grAlarme && a.etat !== 'fuite' && !familier) {
      // L'alarme se propage dans le troupeau: on part avec les autres.
      a.etat = 'fuite'
      a.minuterie = alea.entre(1.2, 2.4)
      poserCible(a, grAlarmeX, grAlarmeZ)
    }

    // -------- application de l'etat
    switch (a.etat) {
      case 'fuite': {
        a.minuterie -= dt
        let fx: number
        let fz: number
        if (a.cible) {
          // Fuite dirigee: on court vers le point d'echappee (loin du predateur).
          fx = a.cible.x - a.pos.x
          fz = a.cible.z - a.pos.z
        } else {
          // Direction opposee au joueur; si l'animal est pile dessus, un axe fixe.
          fx = d > 1e-3 ? dx / d : 1
          fz = d > 1e-3 ? dz / d : 0
        }
        if (!avancer(a, fx, fz, def.vitesseFuite, dt, terrain)) {
          // Coince: on abrege la fuite plutot que de vibrer contre la falaise.
          a.minuterie = Math.min(a.minuterie, 0.2)
        }
        if (a.minuterie <= 0) {
          a.etat = etatOrdinaire(p)
          a.cible = null
        }
        break
      }

      case 'suit': {
        if (d > DISTANCE_SUIVI) {
          avancer(a, -dx, -dz, def.vitesse, dt, terrain)
        } else {
          orienterVers(a, -dx, -dz, dt)
        }
        break
      }

      case 'dort': {
        a.y = yAnimal(terrain, a.kind, a.etat, a.pos.x, a.pos.z)
        break
      }

      case 'boule': {
        // Roule en boule ou rentre dans sa carapace: immobile, et il attend que
        // le joueur s'eloigne franchement avant de se derouler.
        a.minuterie -= dt
        a.y = yAnimal(terrain, a.kind, a.etat, a.pos.x, a.pos.z)
        if (a.minuterie <= 0 && d > def.distanceFuite * 1.5) {
          a.etat = etatOrdinaire(p)
          a.cible = null
        }
        break
      }

      case 'garde': {
        // Il ne recule pas: il fait face, sur place.
        a.minuterie -= dt
        orienterVers(a, -dx, -dz, dt)
        a.y = yAnimal(terrain, a.kind, a.etat, a.pos.x, a.pos.z)
        if (a.minuterie <= 0 && d > def.distanceFuite * 1.4) {
          a.etat = etatOrdinaire(p)
          a.cible = null
        }
        break
      }

      case 'broute': {
        a.minuterie -= dt
        if (a.minuterie <= 0) {
          a.etat = etatOrdinaire(p)
          a.cible = null
        }
        break
      }

      case 'plane': {
        // Grands cercles autour d'un point, ailes fixes. On ne bat jamais.
        a.minuterie -= dt
        if (a.cible === null || a.minuterie <= 0) {
          // Le centre du cercle se pose a un rayon d'ici: l'aigle ne se teleporte
          // pas d'un bord du monde a l'autre, il derive de cercle en cercle.
          const ang = alea.entre(0, Math.PI * 2)
          /*
           * Les bornes sont celles de SON ile, pas celles de l'ile 1. Un aigle
           * de l'ile 2 se voyait sinon ramener le centre de son cercle a
           * x = 469 — mille metres a l'ouest, sur l'autre ile — et repartait
           * vers le large a chaque tirage: une derive permanente vers le
           * couchant.
           *
           * `ang` se tire AVANT le clamp et `minuterie` APRES, tous deux sans
           * condition: rendre les bornes dependantes de l'ile ne consomme donc
           * pas un tirage de plus, et les aigles de l'ile 1 sont identiques au
           * bit pres.
           */
          const boite = boiteDeLIle(a.pos.x)
          const bminX = boite.x0 + MARGE_MONDE + RAYON_PLANE + 2
          const bmaxX = boite.x1 - MARGE_MONDE - RAYON_PLANE - 2
          const bminZ = boite.z0 + MARGE_MONDE + RAYON_PLANE + 2
          const bmaxZ = boite.z1 - MARGE_MONDE - RAYON_PLANE - 2
          const bx = Math.min(bmaxX, Math.max(bminX, a.pos.x + Math.cos(ang) * RAYON_PLANE))
          const bz = Math.min(bmaxZ, Math.max(bminZ, a.pos.z + Math.sin(ang) * RAYON_PLANE))
          poserCible(a, bx, bz)
          a.minuterie = alea.entre(24, 55)
        }
        const c = a.cible as Vec2
        const rx = a.pos.x - c.x
        const rz = a.pos.z - c.z
        let r = Math.hypot(rx, rz)
        const angle = r > 1e-3 ? Math.atan2(rz, rx) : a.phase
        // Sens de rotation fixe par individu: la moitie tourne a l'envers.
        const sens = (a.id & 1) === 0 ? 1 : -1
        // La vitesse tangentielle vaut toujours `vitesse`, quel que soit le rayon.
        const omega = (def.vitesse / Math.max(6, r)) * sens
        // Le rayon converge vers le cercle nominal, sans jamais depasser le pas.
        const pasMax = def.vitesse * dt
        r += Math.min(pasMax, Math.max(-pasMax, RAYON_PLANE - r))
        const na = angle + omega * dt
        const nx = c.x + Math.cos(na) * r
        const nz = c.z + Math.sin(na) * r
        if (placeValide(terrain, a.kind, nx, nz)) {
          const vx = nx - a.pos.x
          const vz = nz - a.pos.z
          a.pos.x = nx
          a.pos.z = nz
          a.y = yAnimal(terrain, a.kind, a.etat, nx, nz)
          orienterVers(a, vx, vz, dt)
        } else {
          a.cible = null
        }
        break
      }

      case 'troupeau': {
        a.minuterie -= dt

        // Une cible posee dans cet etat, c'est un COMPAGNON DE JEU: on le
        // rejoint et on lui tourne autour. Sinon, c'est le troupeau qui se
        // rassemble.
        if (a.cible !== null) {
          const j = compagnonTenu(animaux, a, Math.max(6, p.curiosite * 2))
          if (j < 0 || a.minuterie <= 0) {
            // Le compagnon est parti, ou on a assez joue: on souffle un peu.
            a.etat = 'broute'
            a.minuterie = alea.entre(1.5, 4)
            a.cible = null
            a.compagnon = -1
            break
          }
          const compagnon = animaux[j] as Animal
          poserCible(a, compagnon.pos.x, compagnon.pos.z)
          jouerAutour(a, compagnon.pos.x, compagnon.pos.z, def.vitesse, p, dt, terrain)
          break
        }

        if (grNb > 0) {
          if (pousseeSociale(a, 1.6 + a.taille, p.cohesion * 0.35, besoinMin(def.vitesse, dt))) {
            // On ne va jamais plus loin que ce que la correction demande. A
            // pleine vitesse, une bete loin du joueur (donc mise a jour un tick
            // sur huit) depassait sa correction d'un bon metre et repartait
            // aussitot dans l'autre sens: le troupeau entier gigotait.
            const vGroupe = def.vitesse * (1 + 0.25 * pousseeUrgence)
            avancer(a, pousseeX, pousseeZ, Math.min(vGroupe, pousseeBesoin / dt), dt, terrain)
            break
          }
          // On regarde vers les siens, mais seulement si le centre du groupe est
          // assez loin pour avoir une direction: a un metre du barycentre, cette
          // direction saute d'un voisin a l'autre a chaque tick.
          const dBary = dist(a.pos.x, a.pos.z, grBaryX, grBaryZ)
          if (dBary > REGARD_GROUPE) orienterVers(a, grBaryX - a.pos.x, grBaryZ - a.pos.z, dt * REGARD_LENT)
        }
        if (a.minuterie <= 0) {
          // Troupeau rassemble: on broute ensemble, on joue, ou on avance.
          const tirage = alea.reel()
          if (tirage < CHANCE_JEU_TROUPEAU && p.curiosite > 0) {
            const j = compagnonLePlusProche(animaux, i, p.curiosite)
            if (j >= 0) {
              const duree = alea.entre(JEU_MIN, JEU_MAX)
              a.minuterie = duree
              a.compagnon = j
              poserCible(a, (animaux[j] as Animal).pos.x, (animaux[j] as Animal).pos.z)
              inviter(animaux[j] as Animal, a, duree)
              break
            }
          }
          if (tirage < 0.65) {
            a.etat = 'broute'
            a.minuterie = alea.entre(3, 8)
          } else {
            a.etat = 'errance'
            a.minuterie = alea.entre(4, 9)
          }
          a.cible = null
        }
        break
      }

      case 'nage': {
        // Meme moteur que l'errance, mais dans l'eau, avec la cohesion du banc.
        a.minuterie -= dt
        const banc = social && grNb > 0 && pousseeSociale(a, 1.2 + a.taille, p.cohesion * 0.5, besoinMin(def.vitesse, dt))
        if (a.cible === null) nouvelleCible(a, p, terrain, alea, grBaryX, grBaryZ, social && grNb > 0 ? 0.3 : 0)
        const but = a.cible
        if (but === null) break
        const arriveNage = rayonArrivee(def.vitesse, dt)
        if (dist2(a.pos.x, a.pos.z, but.x, but.z) <= arriveNage * arriveNage) {
          a.cible = null
          // Deux dauphins qui se croisent se tournent autour et font des bonds.
          if (p.curiosite > 0 && alea.chance(CHANCE_JEU)) {
            const j = compagnonLePlusProche(animaux, i, p.curiosite)
            if (j >= 0) {
              const duree = alea.entre(JEU_MIN, JEU_MAX)
              a.etat = 'troupeau'
              a.minuterie = duree
              a.compagnon = j
              poserCible(a, (animaux[j] as Animal).pos.x, (animaux[j] as Animal).pos.z)
              inviter(animaux[j] as Animal, a, duree)
            }
          }
          break
        }
        // Le but et le banc tirent ENSEMBLE, dans une seule direction. Tant
        // qu'ils commandaient a tour de role, un tick sur deux, le poisson
        // faisait la navette entre les deux et n'allait nulle part.
        let nagex = but.x - a.pos.x
        let nagez = but.z - a.pos.z
        const dBut = Math.hypot(nagex, nagez)
        if (dBut > 1e-6) {
          nagex /= dBut
          nagez /= dBut
        }
        if (banc) {
          nagex += pousseeX * POIDS_GROUPE
          nagez += pousseeZ * POIDS_GROUPE
        }
        if (!avancer(a, nagex, nagez, def.vitesse * (banc ? 1 + 0.2 * pousseeUrgence : 1), dt, terrain)) a.cible = null
        else if (Math.abs(a.esquive) > ESQUIVE_ABANDON) a.cible = null
        break
      }

      default: {
        // errance: la vie ordinaire. C'est aussi d'ici que part une partie de jeu.
        a.minuterie -= dt
        if (a.cible === null) {
          nouvelleCible(a, p, terrain, alea, grBaryX, grBaryZ, social && grNb > 0 ? 0.4 : 0)
        }
        const cible = a.cible
        if (cible === null) break
        const v = def.vitesse * saccade(p, a.phase)
        const arrive = rayonArrivee(v, dt)
        if (dist2(a.pos.x, a.pos.z, cible.x, cible.z) <= arrive * arrive) {
          a.cible = null
          // Arrive: on va voir qui passe, on picore, ou on rejoint le troupeau.
          if (p.curiosite > 0 && alea.chance(CHANCE_JEU)) {
            const j = compagnonLePlusProche(animaux, i, p.curiosite)
            if (j >= 0) {
              const duree = alea.entre(JEU_MIN, JEU_MAX)
              a.etat = 'troupeau'
              a.minuterie = duree
              a.compagnon = j
              poserCible(a, (animaux[j] as Animal).pos.x, (animaux[j] as Animal).pos.z)
              inviter(animaux[j] as Animal, a, duree)
              break
            }
          }
          // Les volants ne picorent qu'en se posant, et seulement sur la terre.
          if (ESPECES_VOLANTES.includes(a.kind)) {
            if (p.sePose && solAnimal(terrain, a.pos.x, a.pos.z) && alea.chance(0.35)) {
              a.etat = 'broute'
              a.minuterie = alea.entre(2, 6)
              a.y = yAnimal(terrain, a.kind, a.etat, a.pos.x, a.pos.z)
            }
          } else if (p.social === 'troupeau' || p.social === 'banc') {
            a.etat = etatOrdinaire(p)
            a.minuterie = alea.entre(2, 5)
          } else {
            a.etat = 'broute'
            a.minuterie = alea.entre(2, 6)
          }
          break
        }
        if (v > 0) {
          if (!avancer(a, cible.x - a.pos.x, cible.z - a.pos.z, v, dt, terrain)) a.cible = null
          else if (Math.abs(a.esquive) > ESQUIVE_ABANDON) a.cible = null
        }
        break
      }
    }
  }
}

/**
 * Simulation de la faune pour UN joueur. C'est le cas du jeu solo et du client
 * hors ligne; le monde partage passe par `majAnimauxMulti`.
 *
 * Le tableau d'un seul element est alloue a chaque tick, mais il l'est vingt
 * fois par seconde, pas trois cents: c'est hors de la boucle chaude.
 */
export function majAnimaux(
  animaux: Animal[],
  dtTick: number,
  joueurPos: Vec2,
  terrain: ITerrain,
  h: Horloge,
  alea: Alea,
  appat: ItemId | null,
  jeton: string | null = null,
): void {
  majAnimauxMulti(animaux, dtTick, [{ pos: joueurPos, appat, jeton }], terrain, h, alea)
}

export function animalLePlusProche(animaux: Animal[], x: number, z: number, portee: number): Animal | null {
  let meilleur: Animal | null = null
  let meilleurD2 = portee * portee
  for (const a of animaux) {
    const d2 = dist2(x, z, a.pos.x, a.pos.z)
    if (d2 < meilleurD2) {
      meilleurD2 = d2
      meilleur = a
    }
  }
  return meilleur
}

/** Ce qu'un repas a produit. */
export interface Repas {
  /** L'animal a accepte la friandise: il se met a suivre. */
  accepte: boolean
  /** Ce repas a compte pour l'apprivoisement (le precedent etait assez loin). */
  compte: boolean
  /** Ce repas-ci vient de le rendre familier. */
  apprivoise: boolean
  /** Repas comptes jusqu'ici. */
  repas: number
}

/**
 * Donne a manger.
 *
 * L'animal accepte TOUJOURS ce qui lui plait — on ne repousse personne dans ce
 * jeu — mais le repas ne compte pour l'apprivoisement que s'il est espace du
 * precedent (`DELAI_REPAS`, en temps de jeu). `ami` est le jeton de celui qui
 * nourrit: c'est lui, et lui seul, qui pourra ensuite adopter la bete.
 */
export function nourrir(a: Animal, id: ItemId, tempsJeu: number, ami: string | null = null): Repas {
  if (!ANIMAUX[a.kind].friandises.includes(id)) {
    return { accepte: false, compte: false, apprivoise: false, repas: a.repas }
  }
  a.etat = 'suit'
  a.minuterie = DUREE_SUIVI
  a.cible = null

  const avant = a.repas
  const compte = avant < REPAS_APPRIVOISEMENT && (avant === 0 || tempsJeu - a.dernierRepas >= DELAI_REPAS)
  if (compte) {
    a.repas = avant + 1
    a.dernierRepas = tempsJeu
    // Le lien se noue au premier repas et ne change plus de main tant que
    // l'animal n'a pas ete rendu a la nature.
    if (a.ami === null && ami !== null) a.ami = ami
  }
  return {
    accepte: true,
    compte,
    apprivoise: avant < REPAS_APPRIVOISEMENT && a.repas >= REPAS_APPRIVOISEMENT,
    repas: a.repas,
  }
}

/**
 * Installe un animal chez lui: coin de la maison ou centre de l'enclos.
 *
 * Les regles (a qui appartient la maison, un seul animal par maison, l'enclos
 * est-il ferme) sont decidees dans shared/sim/zoo.ts: ici on ne fait que poser
 * le foyer, pour que la simulation reste ignorante des batiments.
 */
export function installerFoyer(a: Animal, ancre: Vec2, rayon: number, maison: string | null): void {
  a.ancre = { x: ancre.x, z: ancre.z }
  a.rayon = rayon
  a.maison = maison
  a.etat = 'errance'
  a.cible = null
  a.compagnon = -1
}

/**
 * Rend l'animal a la nature. Il reste FAMILIER: on ne defait pas une amitie en
 * ouvrant une porte, et le proprietaire a demande qu'on ne perde jamais rien.
 */
export function libererFoyer(a: Animal): void {
  a.ancre = null
  a.rayon = 0
  a.maison = null
  a.cible = null
  a.etat = 'errance'
}
